/**
 * Layout for "remote" pages — the other end of the SDK adapters.
 * For example, when a dapp uses `tempoWallet()`, it opens this app in an
 * iframe or popup. This layout handles the cross-window messaging,
 * mode detection, and UI chrome for those embedded contexts.
 * Also supports standalone visits (e.g. mobile auth, cli auth, etc) without a parent.
 */

import { Tooltip as base_Tooltip } from '@base-ui/react/tooltip'
import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router'
import { reconnect } from '@wagmi/core'
import { Remote } from 'accounts/react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import type { ReactNode } from 'react'
import { Button, cx, Frame, Hash } from 'regen-ui'
import { formatUnits } from 'viem'
import { useConnection } from 'wagmi'
import * as z from 'zod/mini'
import InfoIcon from '~icons/lucide/info'
import Wallet from '~icons/lucide/wallet'

import * as Assets from '#/lib/assets.js'
import * as Auth from '#/lib/auth.js'
import { remote, wagmiConfig } from '#/lib/config.js'
import * as Currency from '#/lib/currency.js'
import * as Mode from '#/lib/mode.js'
import { AnalyticsProvider } from '#/lib/telemetry/analytics.js'
import * as PostHog from '#/lib/telemetry/posthog.js'
import * as Valuation from '#/lib/valuation.js'
import { Centered } from '#/routes/-layouts/Centered.js'
import * as RequestFrames from '#/routes/remote/-frames/request/index.js'
import * as PostMessage from '#/routes/remote/-lib/PostMessage.js'
import * as RemoteRpc from '#/routes/remote/-lib/RemoteRpc.js'

const money = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 2,
  style: 'currency',
})

export const Route = createFileRoute('/remote')({
  component: RemoteLayout,
  validateSearch: z.looseObject({
    icon: z.optional(z.string()),
    iconDark: z.optional(z.string()),
    mode: z.optional(z.union([z.literal('iframe'), z.literal('popup'), z.literal('standalone')])),
  }),
})

function RemoteLayout() {
  const router = useRouter()
  const search = Route.useSearch()
  // Mode is derived once on mount from search params + window hierarchy.
  const [mode] = useState<Mode.Mode>(() => Mode.get(search))
  // Remote wallet actions require a webAuthn connection.
  const connection = useConnection()
  const address = Auth.getSessionAddress(connection)
  const ready = Remote.useState(remote, (s) => s.ready)
  const origin = Remote.useState(remote, (s) => s.origin)

  Remote.useTheme(remote)

  // Wire up bespoke parent messaging for iframe/popup modes only.
  // Standalone mode (e.g. cli/mobile auth) has no parent to message, and
  // wata-owned pages (post-message) run their own session — `mode` only
  // names their chrome.
  useEffect(() => {
    if (mode === 'standalone' || PostMessage.active()) return

    const unsubscribe = remote.onUserRequest(async ({ origin, request }) => {
      if (!request) return
      void PostHog.capture(
        'sdk_rpc_call',
        { method: request.method, mode },
        { dialog: true, origin: remote.store.getState().origin },
      )
      await reconnect(wagmiConfig as never)
      const existing = router.state.location.search as Record<string, unknown>
      RemoteRpc.open(
        router,
        {
          consumer: RemoteRpc.fromOrigin(origin),
          execute: (request, options) => remote.respond(request, options),
          persist: { kind: 'message' },
          reject: (error) => remote.reject(request, error),
          request,
          resolve: (result) => remote.respond(request, { result }),
        },
        { search: existing },
      )
    })

    remote.ready({ accounts: address ? [address] : [] })
    return () => unsubscribe()
  }, [mode])

  // Hide outlet during route transitions to avoid flash of previous screen.
  const isNavigating = useRouterState({ select: (s) => s.status === 'pending' })
  const rpcId = useRouterState({
    select: (s) => {
      const value = (s.location.search as Record<string, unknown>)[RemoteRpc.searchParam]
      if (typeof value === 'string') return value
      return undefined
    },
  })
  const rpc = useMemo(() => RemoteRpc.get(rpcId), [rpcId])
  const rpcReady = useRpcReady(rpc)
  // The outgoing approval screen can re-render mid-exit after its RPC
  // settled and left the registry (e.g. post-message returning to idle).
  // Keep the last RPC flowing through context so it renders while
  // unmounting; expired detection below still uses the live value.
  const rpc_last = useRef(rpc)
  if (rpc) rpc_last.current = rpc
  const origin_resolved = rpc?.consumer.origin ?? origin
  const name =
    rpc?.consumer.name ??
    rpc?.consumer.host ??
    rpc_last.current?.consumer.name ??
    rpc_last.current?.consumer.host ??
    (origin_resolved ? RemoteRpc.fromOrigin(origin_resolved).host : undefined) ??
    'the requesting app'
  // SSR (and the hydration render) can't see the client-side registry or its
  // sessionStorage backup, so a request is only "expired" once the client has
  // actually checked — otherwise a reload flashes "Request Expired" before
  // the restored approval screen replaces it.
  const hydrated = useSyncExternalStore(
    subscribeHydration,
    () => true,
    () => false,
  )
  const isRpcRoute = useRouterState({
    select: (s) => s.location.pathname.startsWith('/remote/rpc/'),
  })
  const isConnectRoute = useRouterState({
    select: (s) => s.location.pathname === '/remote/rpc/wallet_connect',
  })
  const showConnectedFooter = !isConnectRoute
  // An action approval reached us with no connected account. The request
  // handler reconnects before navigating here, so a settled-but-empty
  // connection means signed-out — a wallet that can't act shouldn't surface
  // an action (sign-in is its own `wallet_connect` route), so show a terminal
  // notice instead of a working-looking approval. Wait for the connection to
  // settle first: `address` is briefly undefined mid-reconnect (e.g. on
  // reload) and would otherwise flash the notice to a signed-in user.
  const signedOut =
    isRpcRoute &&
    !isConnectRoute &&
    !address &&
    !connection.isReconnecting &&
    !connection.isConnecting
  const onDismiss = useCallback(() => {
    if (rpc) return rpc.reject()
    // Expired request: nothing left to answer. Best-effort close for embedded
    // modes; return standalone (e.g. mobile) visitors to the wallet home.
    remote.rejectAll()
    if (mode === 'standalone') void router.navigate({ to: '/' })
  }, [mode, router, rpc])
  const sameHost = useMemo(() => {
    if (typeof window === 'undefined') return false
    if (!origin_resolved) return false
    try {
      return new URL(origin_resolved).host === window.location.host
    } catch {
      return false
    }
  }, [origin_resolved])

  // Embedded dialogs preload approval screens so route transitions do not flash.
  useEffect(() => {
    if (mode === 'standalone') return
    const children = Route.children as Record<string, { fullPath: string }> | undefined
    if (!children) return
    for (const child of Object.values(children))
      router.preloadRoute({ to: child.fullPath } as never).catch(() => {})
  }, [mode, router])

  // Standalone and wata-owned pages render immediately; bespoke
  // iframe/popup chrome waits for the parent handshake.
  if (mode !== 'standalone' && !PostMessage.active() && !ready) return null
  // Hide the outlet during route transitions in every mode: the outgoing
  // approval screen would otherwise re-render after its RPC settled and
  // throw from `RemoteRpc.use()` (e.g. post-message returning to idle).
  if (isNavigating) return null

  return (
    <RemoteRpc.Provider rpc={rpc ?? rpc_last.current}>
      <AnalyticsProvider properties={{ dialog: true, origin: origin_resolved }}>
        <RemoteLayoutInner
          confirmationCode={rpc?.confirmationCode}
          mode={mode}
          name={name}
          onDismiss={onDismiss}
          origin={origin_resolved}
          requestExpired={hydrated && isRpcRoute && !rpc}
          restoring={isRpcRoute && (!hydrated || !rpcReady)}
          rpcId={rpcId}
          search={search}
          showConnectedFooter={showConnectedFooter && !sameHost}
          signedOut={signedOut}
        />
      </AnalyticsProvider>
    </RemoteRpc.Provider>
  )
}

function useRpcReady(rpc: RemoteRpc.Pending | undefined): boolean {
  const [resolved, setResolved] = useState<Promise<void>>()
  useEffect(() => {
    let active = true
    if (!rpc?.ready) return
    void rpc.ready.then(() => {
      if (active) setResolved(rpc.ready)
    }, noop)
    return () => {
      active = false
    }
  }, [rpc])
  return !rpc?.ready || resolved === rpc.ready
}

function RemoteLayoutInner(props: {
  confirmationCode?: string | undefined
  mode: Mode.Mode
  name: string
  onDismiss?: (() => void) | undefined
  origin: string | undefined
  requestExpired: boolean
  restoring: boolean
  rpcId: string | undefined
  search: { icon?: string | undefined }
  showConnectedFooter: boolean
  signedOut: boolean
}) {
  const {
    confirmationCode,
    mode,
    name,
    onDismiss,
    origin,
    requestExpired,
    restoring,
    rpcId,
    search,
    showConnectedFooter,
    signedOut,
  } = props
  const framed = Mode.ensureFramed()
  return (
    <div className="contents has-[[data-route-pending]]:hidden">
      <Page icon={search.icon} mode={mode} onDismiss={onDismiss}>
        <ConfirmDevice key={rpcId} confirmationCode={confirmationCode} mode={mode}>
          <DialogFrame mode={mode} onDismiss={onDismiss} showConnectedFooter={showConnectedFooter}>
            <EnsureVisibility enabled={framed && !!origin}>
              {requestExpired ? (
                <RequestFrames.Result
                  description={
                    <>
                      The request expired.
                      <br />
                      <span className="text-label-13">
                        Return to <span className="text-foreground">{name}</span> for details.
                      </span>
                    </>
                  }
                  title="Request expired"
                  variant="error"
                />
              ) : restoring ? null : signedOut ? (
                <Frame subtitle="Sign in to continue." title="No account connected">
                  <Button onClick={onDismiss ?? noop} size="medium" type="button" variant="primary">
                    Close
                  </Button>
                </Frame>
              ) : (
                <Outlet />
              )}
            </EnsureVisibility>
          </DialogFrame>
        </ConfirmDevice>
      </Page>
    </div>
  )
}

function ConfirmDevice(props: {
  children: ReactNode
  confirmationCode?: string | undefined
  mode: Mode.Mode
}) {
  const { children, confirmationCode, mode } = props
  if (!confirmationCode) return children

  return (
    <div
      className={cx(
        'flex max-w-full flex-col gap-3',
        mode === 'standalone'
          ? 'w-[480px] max-dialog:w-full max-dialog:flex-1'
          : mode === 'iframe'
            ? 'w-[404px] max-dialog:w-full'
            : 'w-full',
      )}
    >
      {children}
      <Frame className="shrink-0" onDismiss={null} style={{ paddingBottom: 0 }}>
        <div className="flex items-center justify-between gap-4 px-5 py-3">
          <div className="flex items-center gap-0.5 text-foreground-secondary">
            <p className="text-label-12">Device confirmation code</p>
            <base_Tooltip.Root disableHoverablePopup>
              <base_Tooltip.Trigger
                delay={0}
                render={
                  <Button.Icon
                    aria-label="About device confirmation codes"
                    size="xs"
                    variant="plain"
                  >
                    <InfoIcon />
                  </Button.Icon>
                }
              />
              <base_Tooltip.Portal>
                <base_Tooltip.Positioner align="center" className="z-50" side="top" sideOffset={8}>
                  <base_Tooltip.Popup className="w-56 rounded-button border border-border bg-secondary px-3 py-2 text-label-12 text-foreground shadow-xs">
                    This code links the request in your browser to the one in your terminal. Only
                    approve if both codes match.
                  </base_Tooltip.Popup>
                </base_Tooltip.Positioner>
              </base_Tooltip.Portal>
            </base_Tooltip.Root>
          </div>
          <code className="shrink-0 font-mono text-label-16 leading-none tracking-[0.12em]">
            {confirmationCode}
          </code>
        </div>
      </Frame>
    </div>
  )
}

/**
 * No-op subscription for the `useSyncExternalStore` hydration flag: the
 * "store" never changes — the hook only exists so the server/hydration render
 * reads the server snapshot (`false`) and the first client pass after
 * hydration reads `true`. Module-level so its identity is stable; an inline
 * arrow would re-subscribe every render.
 */
function subscribeHydration() {
  return () => {}
}

function Page(props: {
  children: ReactNode
  icon?: string | undefined
  mode: Mode.Mode
  onDismiss?: (() => void) | undefined
}) {
  const { children, icon, mode, onDismiss } = props
  // Escape key dismisses the dialog in iframe/popup modes.
  useEffect(() => {
    if (mode !== 'iframe' && mode !== 'popup') return
    if (!onDismiss) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [mode, onDismiss])

  if (mode === 'iframe') return <Backdrop onClick={onDismiss ?? noop}>{children}</Backdrop>

  // Popup: auto-resizes the browser window to fit content.
  if (mode === 'popup') return <PopupPage>{children}</PopupPage>

  // Standalone: full-page centered layout (e.g. payment link).
  return <Centered icon={icon}>{children}</Centered>
}

function PopupPage(props: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const chromeWidth = window.outerWidth - window.innerWidth
    window.resizeTo(360 + chromeWidth, window.outerHeight)
    const observer = new ResizeObserver(() => {
      const height = el.scrollHeight + 2
      const chromeHeight = window.outerHeight - window.innerHeight
      window.resizeTo(360 + chromeWidth, height + chromeHeight)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={ref} className="overflow-hidden">
      {props.children}
    </div>
  )
}

function DialogFrame(props: {
  children: ReactNode
  mode: Mode.Mode
  onDismiss?: (() => void) | undefined
  showConnectedFooter: boolean
}) {
  const { children, onDismiss = noop, showConnectedFooter } = props

  const mode = (() => {
    if (props.mode === 'popup') return 'popup' as const
    if (props.mode === 'standalone') return 'standalone' as const
    return 'iframe' as const
  })()

  // Popup: no sizing wrapper — the OS window is the chrome.
  if (mode === 'popup')
    return showConnectedFooter ? (
      <div className={cx('flex flex-col text-foreground', Frame.variantClassName.plain)}>
        <Frame.Provider onDismiss={onDismiss}>{children}</Frame.Provider>
        <ConnectedAccountFooter />
      </div>
    ) : (
      <Frame variant="plain">
        <Frame.Provider onDismiss={onDismiss}>{children}</Frame.Provider>
      </Frame>
    )

  // Iframe: bottom-sheet on mobile, centered card on desktop.
  if (mode !== 'standalone')
    return (
      <div
        className="w-[404px] max-w-full max-dialog:w-full max-dialog:max-h-[90dvh] max-dialog:overflow-y-auto animate-dialog-card"
        onClick={(e) => e.stopPropagation()}
      >
        {showConnectedFooter ? (
          <div className={cx('flex flex-col text-foreground', Frame.variantClassName.sheet)}>
            <Frame.Provider onDismiss={onDismiss}>{children}</Frame.Provider>
            <ConnectedAccountFooter />
          </div>
        ) : (
          <Frame variant="sheet">
            <Frame.Provider onDismiss={onDismiss}>{children}</Frame.Provider>
          </Frame>
        )}
      </div>
    )

  // Standalone: bordered card, full-page on mobile.
  return (
    <div className="w-[480px] max-w-full max-dialog:w-full max-dialog:flex-1">
      {showConnectedFooter ? (
        <div className={cx('flex flex-col text-foreground', Frame.variantClassName.page)}>
          <Frame.Provider onDismiss={onDismiss}>{children}</Frame.Provider>
          <ConnectedAccountFooter />
        </div>
      ) : (
        <Frame onDismiss={onDismiss} variant="page">
          {children}
        </Frame>
      )}
    </div>
  )
}

function noop() {}

function ConnectedAccountFooter() {
  const address = Auth.useSessionAddress()
  const connection = useConnection()
  const me = Auth.me.useQuery()
  const [mode, setMode] = useState<'address' | 'name'>('name')
  const assets = Assets.useAssets({
    account: address,
    chainId: connection.chainId,
    enabled: typeof window !== 'undefined' && !!address && !!connection.chainId,
    fresh: true,
  })
  const valuation = Valuation.useValuation({
    account: address,
    chainId: connection.chainId,
    enabled: typeof window !== 'undefined' && !!address && !!connection.chainId,
  })
  const total = useMemo(
    () =>
      Valuation.total({
        assets: assets.data ?? [],
        fallback: (assets.data ?? []).reduce((sum, asset) => {
          if (Currency.inferCurrency(asset.symbol) !== 'USD') return sum
          const zoneSum = (asset.zones ?? []).reduce((s, zone) => s + BigInt(zone.balance), 0n)
          const gross = BigInt(asset.balance) + zoneSum
          return sum + Number(formatUnits(gross, asset.decimals))
        }, 0),
        valuation: valuation.data,
      }),
    [assets.data, valuation.data?.amount, valuation.data?.available],
  )

  if (!address) return null

  const label = me.data?.email?.trim() || me.data?.username?.trim()
  const label_matches_address = label?.toLowerCase() === address.toLowerCase()

  return (
    <div className="flex h-9 items-center justify-between gap-4 border-t border-border px-5 text-label-12">
      <div className="flex h-5 min-w-0 items-center gap-1.5 text-foreground-secondary">
        {label && !label_matches_address ? (
          <button
            className="inline-flex h-4 max-w-full min-w-0 items-center rounded-button text-left leading-4 transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              if (event.detail !== 0) return
              setMode((mode) => (mode === 'address' ? 'name' : 'address'))
            }}
            onPointerDown={(event) => {
              if (event.pointerType === 'mouse' && event.button !== 0) return
              event.preventDefault()
              event.stopPropagation()
              setMode((mode) => (mode === 'address' ? 'name' : 'address'))
            }}
            title={address}
            type="button"
          >
            {mode === 'address' ? (
              <Hash className="max-w-full leading-4" min={4} value={address} />
            ) : (
              <span className="min-w-0 truncate leading-4">{label}</span>
            )}
          </button>
        ) : (
          <Hash className="max-w-full leading-4" min={4} value={address} />
        )}
        <Button.Icon
          className="-translate-x-1"
          onClick={() => {
            window.open(new URL('/', window.location.origin), '_blank', 'noopener,noreferrer')
          }}
          variant="plain"
          size="xs"
          title="Open Tempo Wallet"
        >
          <Wallet />
        </Button.Icon>
      </div>
      <p className="flex h-4 shrink-0 items-center tabular-nums leading-4 text-foreground-secondary">
        {assets.isLoading ? (
          <span className="block h-3 w-12 animate-pulse rounded-body bg-skeleton" />
        ) : (
          money.format(total)
        )}
      </p>
    </div>
  )
}

function EnsureVisibility(props: { children: ReactNode; enabled: boolean }) {
  const { children, enabled } = props
  // Wata-owned pages escalate to a popup through the session (and pin the
  // trusted-host check to its origin) instead of the bespoke messenger.
  const wataOrigin = PostMessage.origin()
  const { invokePopup, ref, visible } = Remote.useEnsureVisibility(remote, {
    enabled,
    ...(wataOrigin ? { invokePopup: PostMessage.switchMode, origin: wataOrigin } : {}),
  })

  // IO v2 reports the element as not visible while the dialog's entry
  // animations (backdrop opacity fade + card transform) are running, which
  // would flash the occlusion fallback on every route change. Wait for the
  // animations to settle before trusting the `visible` signal.
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    const id = setTimeout(() => setSettled(true), 300)
    return () => clearTimeout(id)
  }, [])

  if (import.meta.env.MODE === 'test') return children

  const showFallback = !visible && settled

  return (
    <div ref={ref} className="flex flex-1 flex-col">
      {showFallback ? (
        <div className="p-6 text-center">
          <p>Continue in new window</p>
          <p className="text-label-14 text-foreground-secondary">
            The dialog may be occluded. The request will open in a new window.
          </p>
          <Button onClick={invokePopup} size="medium" type="button" variant="primary">
            Continue
          </Button>
        </div>
      ) : (
        children
      )}
    </div>
  )
}

function Backdrop(props: { children: ReactNode; onClick: () => void }) {
  const [visible, setVisible] = useState(false)
  useLayoutEffect(() => {
    requestAnimationFrame(() => setVisible(true))
  }, [])
  return (
    <div
      // Dim and card are one element so they share one entrance fade, mounted
      // with the approval after the SDK reveals the iframe — a page-level scrim
      // would already be present on reveal and snap in as the card animates.
      className="fixed inset-0 flex items-start justify-center pt-4 max-dialog:items-end max-dialog:pt-0 bg-black/50 transition-opacity duration-100 ease-out"
      onClick={props.onClick}
      style={{ opacity: visible ? undefined : 0 }}
    >
      {props.children}
    </div>
  )
}
